import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Inbox, Clock, Trash2, Check, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PageTitle } from './DashboardPages';
import { EmptyState, CompanyAvatar } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { messageService } from '@/services/messages';
import { contactService } from '@/services/contacts';
import { onlyDigits, formatDate } from '@/lib/format';
import { useSeo } from '@/lib/seo';

const BADGE = {
  pendente: { label: 'Em análise', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200' },
  ativa: { label: 'Ativa', cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200' },
  encerrada: { label: 'Encerrada', cls: 'border-white/10 bg-white/5 text-muted-foreground' },
  rejeitada: { label: 'Rejeitada', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]' },
};

const FILTERS = [
  ['pendente', 'Em análise'],
  ['ativa', 'Ativas'],
  ['encerrada', 'Encerradas'],
  ['rejeitada', 'Rejeitadas'],
];

const fmt = (iso) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

function RequestCard({ r, side, busy, onDecide, onClose, onRemove }) {
  const other = side === 'received' ? r.requesterCompany : r.targetCompany;
  const [contacts, setContacts] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const badge = BADGE[r.status] || BADGE.pendente;
  const activeLabel = side === 'sent' && r.status === 'ativa' ? 'Aceita' : badge.label;

  const toggleContacts = async () => {
    if (showContacts) {
      setShowContacts(false);
      return;
    }
    try {
      const c = await contactService.contacts(other.id);
      setContacts(c);
      setShowContacts(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <article className="surface p-5" data-testid="contact-request-card">
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${badge.cls}`}>{activeLabel}</span>
            <span className="text-xs text-muted-foreground">{fmt(r.createdAt)}</span>
          </div>

          <div className="mt-3 flex items-center gap-3">
            {other ? <CompanyAvatar company={other} size={36} /> : null}
            <div className="min-w-0">
              {other ? (
                <Link to={`/empresas/${other.id}`} className="truncate font-medium hover:text-primary">{other.name}</Link>
              ) : (
                <span className="text-sm text-muted-foreground">Empresa indisponível</span>
              )}
              {other && <div className="text-[11px] text-muted-foreground">{other.city} · {other.uf}</div>}
            </div>
          </div>

          {side === 'received' && (
            <div className="mt-3 text-xs text-muted-foreground">
              Pedido de {r.requesterName || 'responsável não informado'}{r.requesterEmail ? ` · ${r.requesterEmail}` : ''}
            </div>
          )}
          {r.message && <p className="mt-2 whitespace-pre-line text-sm text-foreground/90">{r.message}</p>}

          {showContacts && (
            <div className="mt-3 space-y-1 rounded-md border border-emerald-400/20 bg-emerald-400/5 p-3 text-sm" data-testid="contact-revealed">
              {contacts?.whatsapp && <div>WhatsApp: <span className="font-mono">{contacts.whatsapp}</span></div>}
              {contacts?.email && <div>E-mail: <span className="font-mono">{contacts.email}</span></div>}
              {contacts?.cnpj && <div>CNPJ: <span className="font-mono">{contacts.cnpj}</span></div>}
              {!contacts?.whatsapp && !contacts?.email && !contacts?.cnpj && (
                <div className="text-muted-foreground">A empresa ainda não informou contatos.</div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-start gap-2">
          {side === 'received' && r.status === 'pendente' && (
            <>
              <Button size="sm" className="btn-glow" disabled={busy} onClick={() => onDecide(r, 'ativa')} data-testid="contact-accept-button"><Check /> Aceitar</Button>
              <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onDecide(r, 'rejeitada')} data-testid="contact-reject-button"><X /> Recusar</Button>
            </>
          )}
          {side === 'received' && r.status === 'ativa' && (
            <>
              <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onClose(r)} data-testid="contact-close-button"><Lock /> Encerrar serviço</Button>
              <Button size="sm" variant="ghost" className="text-[rgb(255,130,150)] hover:bg-[rgba(255,59,92,0.08)]" disabled={busy} onClick={() => onDecide(r, 'rejeitada')} data-testid="contact-revoke-button"><X /> Revogar acesso</Button>
            </>
          )}
          {side === 'received' && r.status === 'rejeitada' && (
            <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onDecide(r, 'ativa')}><Check /> Liberar acesso</Button>
          )}
          {side === 'sent' && r.status === 'ativa' && other && (
            <Button asChild size="sm" className="btn-glow" data-testid="contact-view-profile-button">
              <Link to={`/empresas/${other.id}`}>Ver perfil e contatos</Link>
            </Button>
          )}          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:bg-[rgba(255,59,92,0.08)] hover:text-[#ff6b84]"
            disabled={busy}
            onClick={() => onRemove(r)}
            aria-label="Remover pedido"
            title="Remover pedido"
            data-testid="contact-remove-button"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </article>
  );
}

export function InboxPage() {
  useSeo({ title: 'Caixa de entrada' });
  const { myCompany, authLoading } = useData();
  const [tab, setTab] = useState('recebidas');
  const [filter, setFilter] = useState('pendente');
  const [received, setReceived] = useState(null);
  const [sent, setSent] = useState(null);
  const [messages, setMessages] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!myCompany?.id) return;
    try {
      const [r, s, m] = await Promise.all([
        contactService.received(myCompany.id),
        contactService.sent(myCompany.id),
        messageService.inbox(myCompany.id),
      ]);
      setReceived(r);
      setSent(s);
      setMessages(m);
    } catch (e) {
      toast.error(e.message);
      setReceived([]);
      setSent([]);
      setMessages([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCompany?.id]);

  const decide = async (r, decision) => {
    setBusy(true);
    try {
      await contactService.decide(r.id, decision);
      toast.success(decision === 'ativa' ? 'Acesso liberado' : 'Pedido recusado');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const close = async (r) => {
    setBusy(true);
    try {
      await contactService.close(r.id);
      toast.success('Serviço encerrado');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    const msg = r.status === 'ativa'
      ? 'Remover este pedido? O acesso aos contatos será revogado.'
      : 'Remover este pedido da caixa?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await contactService.remove(r.id);
      toast.success('Pedido removido');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const markRead = async (m) => {
    if (m.readAt) return;
    try {
      await messageService.markRead(m.id);
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, readAt: new Date().toISOString() } : x)));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removeMessage = async (m) => {
    if (!window.confirm('Apagar esta mensagem? Essa ação não pode ser desfeita.')) return;
    try {
      await messageService.remove(m.id);
      setMessages((list) => list.filter((x) => x.id !== m.id));
      toast.success('Mensagem apagada');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (authLoading) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  if (!myCompany) {
    return (
      <EmptyState
        icon={Inbox}
        title="Cadastre sua empresa"
        text="A caixa de entrada fica disponível depois do cadastro da empresa."
        testId="inbox-no-company"
      />
    );
  }

  const TABS = [
    ['recebidas', 'Pedidos recebidos', received],
    ['enviadas', 'Pedidos enviados', sent],
    ['mensagens', 'Mensagens', messages],
  ];
  const list = tab === 'recebidas' ? received : tab === 'enviadas' ? sent : null;
  const shown = list ? list.filter((r) => r.status === filter) : [];
  const unreadMessages = (messages || []).filter((m) => !m.readAt).length;

  return (
    <div className="max-w-3xl space-y-4" data-testid="inbox-page">
      <PageTitle
        title="Caixa de entrada"
        text="Pedidos de contato entre empresas e mensagens do formulário."
      />

      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map(([v, l, items]) => (
          <Button
            key={v}
            size="sm"
            variant={tab === v ? 'default' : 'secondary'}
            className={tab === v ? 'btn-glow' : 'border border-white/10'}
            onClick={() => { setTab(v); setFilter(v === 'enviadas' ? 'ativa' : 'pendente'); }}
            data-testid={`inbox-tab-${v}`}
          >
            {l}
            <span className="ml-1.5 font-mono text-[11px] opacity-70">
              {v === 'mensagens' ? unreadMessages : (items || []).length}
            </span>
          </Button>
        ))}
      </div>

      {list && (
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => (
            <button
              key={v}
              type="button"
              onClick={() => setFilter(v)}
              className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${filter === v ? 'border-cyan-300/40 bg-cyan-300/10 text-cyan-100' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
            >
              {tab === 'enviadas' && v === 'ativa' ? 'Aceitas' : l}
              <span className="ml-1.5 font-mono opacity-70">{list.filter((r) => r.status === v).length}</span>
            </button>
          ))}
        </div>
      )}

      {list && (
        shown.length ? (
          <div className="space-y-3">
            {shown.map((r) => (
              <RequestCard
                key={r.id}
                r={r}
                side={tab === 'recebidas' ? 'received' : 'sent'}
                busy={busy}
                onDecide={decide}
                onClose={close}
                onRemove={remove}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Inbox}
            title="Nada por aqui"
            text="Nenhum pedido nesta categoria."
            testId="inbox-requests-empty"
          />
        )
      )}

      {tab === 'mensagens' && (
        messages === null ? (
          <div className="text-sm text-muted-foreground">Carregando…</div>
        ) : messages.length === 0 ? (
          <EmptyState icon={Inbox} title="Nenhuma mensagem ainda" text="Quando alguém enviar uma mensagem pelo formulário, ela aparece aqui." testId="inbox-empty" />
        ) : (
          messages.map((m) => {
            const digits = m.senderWhatsapp ? onlyDigits(m.senderWhatsapp) : '';
            const hello = encodeURIComponent(`Olá, ${m.senderName}! Recebi sua mensagem pela Brasil Livre Business sobre ${myCompany.name}.`);
            const subject = encodeURIComponent('Re: contato pela Brasil Livre Business');
            return (
              <article
                key={m.id}
                onClick={() => markRead(m)}
                className={`surface p-5 ${m.readAt ? '' : 'border-cyan-300/30'}`}
                data-testid="inbox-message"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {!m.readAt && <span className="h-2 w-2 rounded-full bg-primary" aria-label="Não lida" />}
                  <span className="font-medium">{m.senderName}</span>
                  <span className="text-xs text-muted-foreground">{m.senderEmail}</span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {fmt(m.createdAt)}
                  </span>
                  <button
                    type="button"
                    aria-label="Apagar mensagem"
                    title="Apagar mensagem"
                    onClick={(e) => { e.stopPropagation(); removeMessage(m); }}
                    className="ml-1 grid h-7 w-7 place-items-center rounded text-muted-foreground hover:bg-[rgba(255,59,92,0.08)] hover:text-[#ff6b84]"
                    data-testid="inbox-delete-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-3 whitespace-pre-line text-sm text-foreground/90">{m.body}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {digits && (
                    <Button asChild size="sm" className="btn-glow" onClick={(e) => e.stopPropagation()}>
                      <a href={`https://wa.me/55${digits}?text=${hello}`} target="_blank" rel="noopener noreferrer">
                        <MessageCircle /> Responder no WhatsApp
                      </a>
                    </Button>
                  )}
                  <Button asChild size="sm" variant="secondary" className="border border-white/10" onClick={(e) => e.stopPropagation()}>
                    <a href={`mailto:${m.senderEmail}?subject=${subject}`}>
                      <Mail /> Responder por e-mail
                    </a>
                  </Button>
                </div>
              </article>
            );
          })
        )
      )}
    </div>
  );
}

export default InboxPage;